import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import {
    Bell,
    BriefcaseBusiness,
    Building2,
    ChevronDown,
    FileText,
    GraduationCap,
    Home,
    Laptop,
    LayoutDashboard,
    LogOut,
    Menu,
    Settings,
    User,
    UserRound,
    Users,
    X,
    Languages,
} from "lucide-react";

import { useTranslation } from "react-i18next";

import certedLogo from "../asset/logo_certed.png";

import "./Navbar.css";


function Navbar() {

    const navigate = useNavigate();

    /* =====================================================
       TRANSLATION
    ===================================================== */

    const { t, i18n } = useTranslation();


    /* =====================================================
       STATES
    ===================================================== */

    const [isLogin, setIsLogin] = useState(false);
    const [workOpen, setWorkOpen] = useState(false);
    const [manpowerOpen, setManpowerOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);


    /* =====================================================
       REFS
    ===================================================== */

    const workRef = useRef(null);
    const manpowerRef = useRef(null);
    const profileRef = useRef(null);


    /* =====================================================
       DROPDOWN OPTIONS
       (change the `path` values to your real routes)
    ===================================================== */

    const workOptions = [
        {
            key: "jobs",
            path: "/jobs",
            icon: BriefcaseBusiness,
            iconClass: "jobs-icon",
            title: t("navbar.findJobs"),
            description: t("navbar.findJobsDescription"),
        },
        {
            key: "freelance",
            path: "/freelance",
            icon: Users,
            iconClass: "freelance-icon",
            title: t("navbar.freelanceProjects"),
            description: t("navbar.freelanceProjectsDescription"),
        },
    ];


    const manpowerOptions = [
        {
            key: "trainer",
            path: "/trainers",
            icon: UserRound,
            iconClass: "jobs-icon",
            title: t("navbar.hireTrainer", {
                defaultValue: "Trainer",
            }),
            description: t("navbar.hireTrainerDescription", {
                defaultValue: "Corporate and technical trainers",
            }),
        },
        {
            key: "faculties",
            path: "/faculties",
            icon: GraduationCap,
            iconClass: "freelance-icon",
            title: t("navbar.hireFaculties", {
                defaultValue: "Faculties",
            }),
            description: t("navbar.hireFacultiesDescription", {
                defaultValue: "Experienced teaching faculty",
            }),
        },
        {
            key: "home-tutor",
            path: "/home-tutors",
            icon: Home,
            iconClass: "jobs-icon",
            title: t("navbar.hireHomeTutor", {
                defaultValue: "Home Tutor",
            }),
            description: t("navbar.hireHomeTutorDescription", {
                defaultValue: "One-to-one tutors at your doorstep",
            }),
        },
        {
            key: "freelancer",
            path: "/freelance-Users",
            icon: Laptop,
            iconClass: "freelance-icon",
            title: t("navbar.hireFreelancer", {
                defaultValue: "Freelancer",
            }),
            description: t("navbar.hireFreelancerDescription", {
                defaultValue: "Hire independent professionals",
            }),
        },
    ];


    /* =====================================================
       LANGUAGE CHANGE
    ===================================================== */

    const changeLanguage = async (language) => {

        try {

            await i18n.changeLanguage(language);

            const selectedLanguage = language.split("-")[0];

            document.documentElement.dir =
                selectedLanguage === "ar" ? "rtl" : "ltr";

            document.documentElement.lang = selectedLanguage;

        } catch (error) {

            console.error("Language change failed:", error);

        }

    };


    /* =====================================================
       SET INITIAL DIRECTION
    ===================================================== */

    useEffect(() => {

        const currentLanguage = i18n.language?.split("-")[0] || "en";

        document.documentElement.dir =
            currentLanguage === "ar" ? "rtl" : "ltr";

        document.documentElement.lang = currentLanguage;

    }, [i18n.language]);


    /* =====================================================
       GET USER
    ===================================================== */

    const getUser = () => {

        try {

            const storedUser = localStorage.getItem("user");

            if (!storedUser) {
                return {};
            }

            const parsedUser = JSON.parse(storedUser);

            if (parsedUser && typeof parsedUser === "object") {
                return parsedUser;
            }

            return {};

        } catch (error) {

            console.error("Unable to parse user:", error);

            return {};

        }

    };


    const user = getUser();


    /* =====================================================
       ROLE
    ===================================================== */

    /*
       Supported Roles:

       TRAINER
       PARTNER
       RECRUITER
       ADMIN
       EMPLOYEE
    */

    const normalizedRole = String(
        user?.role ||
        user?.userRole ||
        user?.roles?.[0] ||
        ""
    )
        .trim()
        .toUpperCase();


    /* =====================================================
       ROLE FLAGS
    ===================================================== */

    const isTrainer = normalizedRole === "TRAINER";


    /* =====================================================
       ROLE DASHBOARD
    ===================================================== */

    const getDashboardPath = () => {

        switch (normalizedRole) {

            case "TRAINER":
                return "/trainer-dashboard";

            case "PARTNER":
                return "/partner-dashboard";

            case "RECRUITER":
                return "/partner-dashboard";

            case "ADMIN":
                return "/dashboard";

            case "EMPLOYEE":
                return "/employee-dashboard";

            default:
                return "/dashboard";
        }

    };


    /* =====================================================
       CHECK LOGIN
    ===================================================== */

    useEffect(() => {

        const checkLogin = () => {

            const token = localStorage.getItem("token");

            setIsLogin(Boolean(token));

        };

        checkLogin();

        window.addEventListener("storage", checkLogin);

        return () => {

            window.removeEventListener("storage", checkLogin);

        };

    }, []);


    /* =====================================================
       USER NAME
    ===================================================== */

    const fullName =
        typeof user?.fullName === "string" && user.fullName.trim()

            ? user.fullName.trim()

            : typeof user?.name === "string" && user.name.trim()

            ? user.name.trim()

            : t("common.account");


    /* =====================================================
       USER EMAIL
    ===================================================== */

    const userEmail =
        typeof user?.email === "string" && user.email.trim()

            ? user.email.trim()

            : t("common.userAccount");


    /* =====================================================
       INITIALS
    ===================================================== */

    const initials =
        fullName
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((name) => name.charAt(0).toUpperCase())
            .join("") || "U";


    /* =====================================================
       OUTSIDE CLICK
    ===================================================== */

    useEffect(() => {

        const handleOutsideClick = (event) => {

            if (
                workRef.current &&
                !workRef.current.contains(event.target)
            ) {
                setWorkOpen(false);
            }

            if (
                manpowerRef.current &&
                !manpowerRef.current.contains(event.target)
            ) {
                setManpowerOpen(false);
            }

            if (
                profileRef.current &&
                !profileRef.current.contains(event.target)
            ) {
                setProfileOpen(false);
            }

        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {

            document.removeEventListener(
                "mousedown",
                handleOutsideClick
            );

        };

    }, []);


    /* =====================================================
       NAVIGATION
    ===================================================== */

    const goTo = (path) => {

        navigate(path);

        setWorkOpen(false);
        setManpowerOpen(false);
        setProfileOpen(false);
        setMobileOpen(false);

    };


    /* =====================================================
       LOGOUT
    ===================================================== */

    const logout = () => {

        localStorage.removeItem("token");
        localStorage.removeItem("user");

        setIsLogin(false);

        setProfileOpen(false);
        setWorkOpen(false);
        setManpowerOpen(false);
        setMobileOpen(false);

        navigate("/login");

    };


    /* =====================================================
       CLOSE MOBILE MENU
    ===================================================== */

    const closeMobileMenu = () => {

        setMobileOpen(false);
        setWorkOpen(false);
        setManpowerOpen(false);
        setProfileOpen(false);

    };


    /* =====================================================
       TOGGLE MOBILE MENU
    ===================================================== */

    const toggleMobileMenu = () => {

        setMobileOpen((prev) => !prev);

        setWorkOpen(false);
        setManpowerOpen(false);
        setProfileOpen(false);

    };


    /* =====================================================
       CURRENT LANGUAGE
    ===================================================== */

    const currentLanguage = i18n.language?.split("-")[0] || "en";


    /* =====================================================
       DROPDOWN OPTION RENDERER
    ===================================================== */

    const renderDropdownOptions = (options) =>
        options.map((option) => {

            const Icon = option.icon;

            return (

                <button
                    key={option.key}
                    type="button"
                    className="work-option"
                    onClick={() => goTo(option.path)}
                >

                    <div className={`work-option-icon ${option.iconClass}`}>
                        <Icon size={18} />
                    </div>

                    <div className="work-option-content">
                        <strong>{option.title}</strong>
                        <span>{option.description}</span>
                    </div>

                    <span className="work-option-arrow">→</span>

                </button>

            );

        });


    return (

        <header className="certed-navbar">

            <div className="navbar-container">


                {/* =================================================
                    LOGO
                ================================================= */}

                <div
                    className="certed-logo-area"
                    onClick={() => goTo("/")}
                    role="button"
                    tabIndex={0}
                    aria-label={t("navbar.home")}

                    onKeyDown={(event) => {

                        if (event.key === "Enter" || event.key === " ") {

                            event.preventDefault();

                            goTo("/");

                        }

                    }}
                >

                    <img
                        src={certedLogo}
                        alt="CertED Technologies"
                        className="certed-logo-image"
                    />

                </div>


                {/* =================================================
                    DESKTOP NAVIGATION
                ================================================= */}

                <nav
                    className="navbar-navigation"
                    aria-label={t("common.mainNavigation")}
                >


                    {/* =============================================
                        FIND WORK
                    ============================================= */}

                    <div className="find-work-wrapper" ref={workRef}>

                        <button
                            type="button"
                            className={`navbar-link find-work-button ${
                                workOpen ? "selected" : ""
                            }`}

                            onClick={() => {
                                setWorkOpen((prev) => !prev);
                                setManpowerOpen(false);
                            }}

                            aria-expanded={workOpen}
                        >

                            <span>{t("navbar.findWork")}</span>

                            <ChevronDown
                                size={14}
                                className={workOpen ? "chevron-up" : ""}
                            />

                        </button>

                        {workOpen && (

                            <div className="find-work-dropdown">

                                <div className="dropdown-top">
                                    <h3>{t("navbar.findWork")}</h3>
                                    <p>{t("navbar.findWorkDescription")}</p>
                                </div>

                                <div className="dropdown-items">
                                    {renderDropdownOptions(workOptions)}
                                </div>

                            </div>

                        )}

                    </div>


                    {/* =============================================
                        HIRE TALENT
                    ============================================= */}

                    <div className="find-work-wrapper" ref={manpowerRef}>

                        <button
                            type="button"
                            className={`navbar-link find-work-button ${
                                manpowerOpen ? "selected" : ""
                            }`}

                            onClick={() => {
                                setManpowerOpen((prev) => !prev);
                                setWorkOpen(false);
                            }}

                            aria-expanded={manpowerOpen}
                        >

                            <span>
                                {t("navbar.hireTalent", {
                                    defaultValue: "Hire Talent",
                                })}
                            </span>

                            <ChevronDown
                                size={14}
                                className={manpowerOpen ? "chevron-up" : ""}
                            />

                        </button>

                        {manpowerOpen && (

                            <div className="find-work-dropdown">

                                <div className="dropdown-top">

                                    <h3>
                                        {t("navbar.hireTalent", {
                                            defaultValue: "Hire Talent",
                                        })}
                                    </h3>

                                    <p>
                                        {t("navbar.hireTalentDescription", {
                                            defaultValue:
                                                "Hire the right talent for your team",
                                        })}
                                    </p>

                                </div>

                                <div className="dropdown-items">
                                    {renderDropdownOptions(manpowerOptions)}
                                </div>

                            </div>

                        )}

                    </div>


                    {/* =============================================
                        FIND FREELANCERS
                    ============================================= */}

                    <NavLink
                        to="/freelance-Users"

                        className={({ isActive }) =>
                            `navbar-link freelance-nav-link ${
                                isActive ? "active" : ""
                            }`
                        }
                    >

                        <Users size={15} />

                        <span>{t("navbar.findFreelancers")}</span>

                    </NavLink>


                    {/* =============================================
                        HIRE TRAINERS
                    ============================================= */}

                    {!isLogin && (

                        <NavLink
                            to="/partner-register"

                            className={({ isActive }) =>
                                `navbar-link ${isActive ? "active" : ""}`
                            }
                        >

                            <Building2 size={15} />

                            <span>{t("navbar.hireTrainers")}</span>

                        </NavLink>

                    )}


                    {/* =============================================
                        APPLICATIONS
                        ONLY TRAINER
                    ============================================= */}

                    {isLogin && isTrainer && (

                        <NavLink
                            to="/trainer/my-application"

                            className={({ isActive }) =>
                                `navbar-link ${isActive ? "active" : ""}`
                            }
                        >

                            <FileText size={15} />

                            <span>{t("navbar.applications")}</span>

                        </NavLink>

                    )}

                </nav>


                {/* =================================================
                    RIGHT SIDE
                ================================================= */}

                <div className="navbar-right">


                    {/* =============================================
                        LANGUAGE SELECTOR
                    ============================================= */}

                    <div className="language-selector">

                        <Languages size={16} />

                        <select
                            value={currentLanguage}

                            onChange={(event) =>
                                changeLanguage(event.target.value)
                            }

                            aria-label={t("common.selectLanguage")}
                        >

                            <option value="en">🇬🇧 English</option>
                            <option value="hi">🇮🇳 हिन्दी</option>
                            <option value="de">🇩🇪 Deutsch</option>
                            <option value="fr">🇫🇷 Français</option>
                            <option value="ar">🇦🇪 العربية</option>

                        </select>

                    </div>


                    {/* =============================================
                        LOGGED OUT
                    ============================================= */}

                    {!isLogin ? (

                        <>

                            <NavLink to="/login" className="login-link">
                                {t("navbar.login")}
                            </NavLink>

                            <NavLink
                                to="/trainer-register"
                                className="join-trainer-button"
                            >

                                <UserRound size={15} />

                                <span>{t("navbar.joinTrainer")}</span>

                            </NavLink>

                        </>

                    ) : (

                        <>

                            {/* NOTIFICATIONS */}

                            <button
                                type="button"
                                className="notification-button"
                                onClick={() => goTo("/notifications")}
                                title={t("navbar.notifications")}
                                aria-label={t("navbar.notifications")}
                            >

                                <Bell size={19} />

                                <span className="notification-badge">1</span>

                            </button>


                            {/* PROFILE */}

                            <div className="profile-wrapper" ref={profileRef}>

                                <button
                                    type="button"

                                    className={`profile-button ${
                                        isTrainer && profileOpen
                                            ? "profile-open"
                                            : ""
                                    }`}

                                    onClick={() => {

                                        /*
                                         * TRAINER: open profile dropdown
                                         * OTHER ROLES: open own dashboard
                                         */

                                        if (isTrainer) {

                                            setProfileOpen((prev) => !prev);

                                        } else {

                                            goTo(getDashboardPath());

                                        }

                                    }}

                                    aria-expanded={
                                        isTrainer ? profileOpen : undefined
                                    }

                                    aria-label={
                                        isTrainer
                                            ? t("navbar.myAccount")
                                            : `${normalizedRole} Dashboard`
                                    }
                                >

                                    <div className="profile-avatar">
                                        {initials}
                                    </div>

                                    <div className="profile-details">

                                        <strong>{fullName}</strong>

                                        <span>
                                            {isTrainer
                                                ? t("navbar.myAccount")
                                                : normalizedRole
                                                    ? normalizedRole
                                                    : t("navbar.myAccount")}
                                        </span>

                                    </div>

                                    {isTrainer && (

                                        <ChevronDown
                                            size={14}
                                            className={
                                                profileOpen ? "chevron-up" : ""
                                            }
                                        />

                                    )}

                                </button>


                                {/* TRAINER PROFILE DROPDOWN */}

                                {isTrainer && profileOpen && (

                                    <div className="profile-dropdown">

                                        <div className="profile-summary">

                                            <div className="profile-avatar profile-avatar-large">
                                                {initials}
                                            </div>

                                            <div className="profile-summary-text">
                                                <strong>{fullName}</strong>
                                                <span>{userEmail}</span>
                                            </div>

                                        </div>

                                        <div className="menu-divider" />

                                        <button
                                            type="button"
                                            onClick={() =>
                                                goTo("/trainer-dashboard")
                                            }
                                        >
                                            <LayoutDashboard size={16} />
                                            <span>{t("navbar.dashboard")}</span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                goTo("/trainer/profile")
                                            }
                                        >
                                            <User size={16} />
                                            <span>{t("navbar.myProfile")}</span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                goTo("/trainer/my-applications")
                                            }
                                        >
                                            <FileText size={16} />
                                            <span>
                                                {t("navbar.myApplications")}
                                            </span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                goTo("/notifications")
                                            }
                                        >
                                            <Bell size={16} />
                                            <span>
                                                {t("navbar.notifications")}
                                            </span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => goTo("/settings")}
                                        >
                                            <Settings size={16} />
                                            <span>{t("navbar.settings")}</span>
                                        </button>

                                        <div className="menu-divider" />

                                        <button
                                            type="button"
                                            className="logout-button"
                                            onClick={logout}
                                        >
                                            <LogOut size={16} />
                                            <span>{t("navbar.signOut")}</span>
                                        </button>

                                    </div>

                                )}

                            </div>

                        </>

                    )}


                    {/* MOBILE MENU BUTTON */}

                    <button
                        type="button"
                        className="mobile-menu-button"
                        onClick={toggleMobileMenu}

                        aria-label={
                            mobileOpen
                                ? t("common.closeMenu")
                                : t("common.openMenu")
                        }

                        aria-expanded={mobileOpen}
                    >

                        {mobileOpen ? <X size={21} /> : <Menu size={21} />}

                    </button>

                </div>

            </div>


            {/* =====================================================
                MOBILE NAVIGATION
            ===================================================== */}

            {mobileOpen && (

                <div
                    className="mobile-navigation"
                    aria-label={t("common.mobileNavigation")}
                >

                    {/* FIND JOBS */}

                    <NavLink
                        to="/jobs"
                        onClick={closeMobileMenu}
                        className="mobile-link"
                    >
                        <BriefcaseBusiness size={17} />
                        <span>{t("navbar.findJobs")}</span>
                    </NavLink>


                    {/* HIRE TALENT OPTIONS */}

                    {manpowerOptions.map((option) => {

                        const Icon = option.icon;

                        return (

                            <NavLink
                                key={option.key}
                                to={option.path}
                                onClick={closeMobileMenu}
                                className="mobile-link"
                            >
                                <Icon size={17} />
                                <span>{option.title}</span>
                            </NavLink>

                        );

                    })}


                    {/* FIND FREELANCERS */}

                    <NavLink
                        to="/freelance"
                        onClick={closeMobileMenu}
                        className="mobile-link"
                    >
                        <Users size={17} />
                        <span>{t("navbar.findFreelancers")}</span>
                    </NavLink>


                    {/* LOGGED OUT */}

                    {!isLogin && (

                        <>

                            <NavLink
                                to="/partner-register"
                                onClick={closeMobileMenu}
                                className="mobile-link"
                            >
                                <Building2 size={17} />
                                <span>{t("navbar.hireTrainers")}</span>
                            </NavLink>

                            <NavLink
                                to="/login"
                                onClick={closeMobileMenu}
                                className="mobile-login-button"
                            >
                                {t("navbar.login")}
                            </NavLink>

                            <NavLink
                                to="/trainer-register"
                                onClick={closeMobileMenu}
                                className="mobile-join-button"
                            >
                                <UserRound size={16} />
                                <span>{t("navbar.joinTrainer")}</span>
                            </NavLink>

                        </>

                    )}


                    {/* LOGGED IN */}

                    {isLogin && (

                        <>

                            <NavLink
                                to={getDashboardPath()}
                                onClick={closeMobileMenu}
                                className="mobile-link"
                            >
                                <LayoutDashboard size={17} />
                                <span>{t("navbar.dashboard")}</span>
                            </NavLink>

                            {isTrainer && (

                                <NavLink
                                    to="/trainer/my-applications"
                                    onClick={closeMobileMenu}
                                    className="mobile-link"
                                >
                                    <FileText size={17} />
                                    <span>{t("navbar.myApplications")}</span>
                                </NavLink>

                            )}

                            {isTrainer && (

                                <NavLink
                                    to="/trainer/profile"
                                    onClick={closeMobileMenu}
                                    className="mobile-link"
                                >
                                    <User size={17} />
                                    <span>{t("navbar.myProfile")}</span>
                                </NavLink>

                            )}

                            <NavLink
                                to="/notifications"
                                onClick={closeMobileMenu}
                                className="mobile-link"
                            >
                                <Bell size={17} />
                                <span>{t("navbar.notifications")}</span>
                            </NavLink>

                            <NavLink
                                to="/settings"
                                onClick={closeMobileMenu}
                                className="mobile-link"
                            >
                                <Settings size={17} />
                                <span>{t("navbar.settings")}</span>
                            </NavLink>

                            <button
                                type="button"
                                className="mobile-logout"
                                onClick={logout}
                            >
                                <LogOut size={17} />
                                <span>{t("navbar.signOut")}</span>
                            </button>

                        </>

                    )}

                </div>

            )}

        </header>

    );

}


export default Navbar;